import { Box, Grid, Header, Icon, Link, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useMemo } from 'react';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import AppNameWithLink from 'src/components/generic-components/AppNameWithLink';
import { TableEmptyState } from 'src/components/generic-components/TableCommons';

interface AllAppsGridViewProps {
  items: readonly ApplicationFlatTableEntity[];
}

export const AllAppsGridView: React.FC<AllAppsGridViewProps> = ({ items }) => {
  const { distinctModules } = useAppContext();

  const groupedByModule = useMemo(() => {
    const groups: Record<string, ApplicationFlatTableEntity[]> = {};
    items.forEach((item) => {
      const module = item.glAccount && item.glAccount !== 'N/A' ? item.glAccount : 'Other';
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(item);
    });
    const ordered = distinctModules.filter((m) => groups[m]);
    const unlisted = Object.keys(groups).filter((m) => !distinctModules.includes(m));
    return [...ordered, ...unlisted].map((m) => [m, groups[m]] as [string, ApplicationFlatTableEntity[]]);
  }, [items, distinctModules]);

  if (items.length === 0) {
    return <TableEmptyState resourceName="Applications" />;
  }

  return (
    <SpaceBetween size="l">
      {groupedByModule.map(([module, moduleItems]) => (
        <div key={module}>
          <Header variant="h3">{module}</Header>
          <hr className="all-apps-grid-divider" />
          <Box padding={{ top: 's' }}>
            <Grid gridDefinition={moduleItems.map(() => ({ colspan: { default: 12, xs: 6, m: 4 } }))}>
              {moduleItems.map((item) => (
                <div key={item.id} className="all-apps-grid-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <AppNameWithLink
                      appName={item.applicationName}
                      appId={item.id}
                      embeddingEnabled={item.embeddingEnabled}
                      applicationLink={item.applicationLink}
                      fontSize="heading-s"
                    />
                    {item.mcpPath && (
                      <Link href={item.mcpPath} variant="info" ariaLabel={`MCP page for ${item.applicationName}`}>
                        <Icon name="gen-ai" />
                      </Link>
                    )}
                  </div>
                  {item.shortApplicationDescription && (
                    <Box variant="p" color="text-body-secondary" fontSize="body-s" padding={{ top: 'xxs' }}>
                      {item.shortApplicationDescription}
                    </Box>
                  )}
                </div>
              ))}
            </Grid>
          </Box>
        </div>
      ))}
    </SpaceBetween>
  );
};
